/**
 * Image URL Cache Utility
 * Caches Firebase Storage download URLs to avoid repeated getDownloadURL() calls
 * and improves image loading performance
 */

const URL_CACHE_KEY = 'firebase_image_urls';
const CACHE_EXPIRY_DAYS = 30; // URLs are valid for a long time, but we'll refresh periodically

/**
 * Get cached image URL
 * @param {string} storagePath - The Firebase Storage path
 * @returns {string|null} - Cached URL or null if not found/expired
 */
export const getCachedImageUrl = (storagePath) => {
    try {
        const cache = JSON.parse(localStorage.getItem(URL_CACHE_KEY) || '{}');
        const cached = cache[storagePath];
        
        if (cached && cached.url) {
            // Check if cache is still valid (not expired)
            const now = Date.now();
            if (cached.expiry && cached.expiry > now) {
                return cached.url;
            } else {
                // Expired, remove it
                delete cache[storagePath];
                localStorage.setItem(URL_CACHE_KEY, JSON.stringify(cache));
            }
        }
    } catch (error) {
        console.warn('Error reading image URL cache:', error);
    }
    
    return null;
};

/**
 * Cache an image URL
 * @param {string} storagePath - The Firebase Storage path
 * @param {string} url - The download URL
 */
export const cacheImageUrl = (storagePath, url) => {
    try {
        const cache = JSON.parse(localStorage.getItem(URL_CACHE_KEY) || '{}');
        const expiry = Date.now() + (CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
        
        cache[storagePath] = {
            url,
            expiry,
            cachedAt: Date.now()
        };
        
        // Limit cache size to prevent localStorage overflow (keep last 1000 entries)
        const entries = Object.entries(cache);
        if (entries.length > 1000) {
            // Sort by cachedAt and keep most recent 1000
            entries.sort((a, b) => b[1].cachedAt - a[1].cachedAt);
            const limitedCache = Object.fromEntries(entries.slice(0, 1000));
            localStorage.setItem(URL_CACHE_KEY, JSON.stringify(limitedCache));
        } else {
            localStorage.setItem(URL_CACHE_KEY, JSON.stringify(cache));
        }
    } catch (error) {
        console.warn('Error caching image URL:', error);
        // If localStorage is full, try to clear old entries
        try {
            clearExpiredCache();
        } catch (e) {
            console.error('Failed to clear expired cache:', e);
        }
    }
};

/**
 * Clear expired cache entries
 */
export const clearExpiredCache = () => {
    try {
        const cache = JSON.parse(localStorage.getItem(URL_CACHE_KEY) || '{}');
        const now = Date.now();
        let cleared = false;
        
        Object.keys(cache).forEach(key => {
            if (cache[key].expiry && cache[key].expiry <= now) {
                delete cache[key];
                cleared = true;
            }
        });
        
        if (cleared) {
            localStorage.setItem(URL_CACHE_KEY, JSON.stringify(cache));
        }
    } catch (error) {
        console.warn('Error clearing expired cache:', error);
    }
};

/**
 * Clear all cached URLs
 */
export const clearImageUrlCache = () => {
    try {
        localStorage.removeItem(URL_CACHE_KEY);
    } catch (error) {
        console.warn('Error clearing image URL cache:', error);
    }
};

/**
 * Extract storage path from Firebase Storage URL
 * @param {string} url - Firebase Storage download URL
 * @returns {string|null} - Storage path or null
 */
export const extractStoragePathFromUrl = (url) => {
    try {
        // Firebase Storage URLs format:
        // https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{encodedPath}?alt=media&token={token}
        const match = url.match(/\/o\/([^?]+)/);
        if (match) {
            return decodeURIComponent(match[1]);
        }
    } catch (error) {
        console.warn('Error extracting storage path from URL:', error);
    }
    return null;
};

